import type {
  AccountantInboxAckItem,
  AccountantInboxBoletoItem,
  AccountantInboxObligationItem,
} from "@fiscalo/shared-types";

/**
 * Visual + linguistic metadata shared by the bandeja preview (cartera
 * home) and the full-list pages (`/accountant/inbox/*`). Keeps the
 * shape of "a row in the bandeja" consistent across both surfaces so
 * we never end up with two slightly-different renderings of the same
 * underlying item.
 */
export interface BandejaSectionMeta {
  /** Section key, mirrors the URL slug. */
  key: "acks" | "boletos" | "obligations";
  /** User-visible title (es-UY primary). */
  title: string;
  /** One-line description shown beneath the title. */
  description: string;
  /** /accountant/inbox/<key> route. */
  route: string;
}

export const BANDEJA_SECTIONS: Record<
  BandejaSectionMeta["key"],
  BandejaSectionMeta
> = {
  acks: {
    key: "acks",
    title: "Comprobantes recibidos sin aceptar",
    description:
      "Comprobantes que esperan tu aceptación. El plazo DGI son 20 días corridos.",
    route: "/accountant/inbox/acks",
  },
  boletos: {
    key: "boletos",
    title: "Boletos pendientes",
    description:
      "Obligaciones DGI a las que les falta el boleto de pago adjunto.",
    route: "/accountant/inbox/boletos",
  },
  obligations: {
    key: "obligations",
    title: "Próximos vencimientos",
    description: "Obligaciones DGI y BPS que vencen en los próximos 30 días.",
    route: "/accountant/inbox/obligations",
  },
};

const DEFAULT_REGIONAL = "es-UY";

function pluralize(value: number, singular: string, plural: string): string {
  return value === 1 ? singular : plural;
}

export function ackUrgencyLabel(item: AccountantInboxAckItem): string {
  if (item.overdue) {
    return `Vencido hace ${item.daysPending - 20} ${pluralize(item.daysPending - 20, "día", "días")}`;
  }
  if (item.daysPending >= 17) {
    return `Quedan ${20 - item.daysPending} ${pluralize(20 - item.daysPending, "día", "días")}`;
  }
  return `${item.daysPending} ${pluralize(item.daysPending, "día", "días")} pendiente`;
}

export function obligationUrgencyLabel(
  item: AccountantInboxBoletoItem | AccountantInboxObligationItem,
): string {
  if (item.overdue) {
    return `Venció hace ${Math.abs(item.daysUntilDue)} ${pluralize(Math.abs(item.daysUntilDue), "día", "días")}`;
  }
  if (item.daysUntilDue === 0) return "Vence hoy";
  if (item.daysUntilDue === 1) return "Vence mañana";
  return `En ${item.daysUntilDue} ${pluralize(item.daysUntilDue, "día", "días")}`;
}

export function formatMoneyEs(currency: string, amount: number): string {
  const sign = amount < 0 ? "-" : "";
  const abs = Math.abs(amount);
  return `${sign}${currency} ${abs.toLocaleString(DEFAULT_REGIONAL, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function tenantInitials(name: string | null): string {
  const trimmed = name?.trim() ?? "";
  if (!trimmed) return "—";
  return trimmed.charAt(0).toUpperCase();
}
